import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useT } from '../i18n/i18n';
import { refreshAiAccess, type AiAccess } from './useAiAccess';

export default function AiTermsGate({ access }: { access: AiAccess }) {
  const t = useT();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      await api('/ai/terms', { method: 'POST', token: true, body: { version: access.termsVersion } });
      refreshAiAccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card ai-terms" aria-labelledby="ai-terms-h">
      <h2 id="ai-terms-h" style={{ marginTop: 0 }}>{t('Before you use EasyLearning AI')}</h2>
      <h3>{t('What it is')}</h3>
      <p>{t('EasyLearning AI writes teaching advice, suggestions and new games. It can be wrong or miss something. Check what it gives you before you use it with your learner.')}</p>
      <h3>{t('What it is not')}</h3>
      <p>{t('It is not medical, therapy or legal advice. It does not diagnose anything and does not replace a doctor, therapist or teacher who knows your learner.')}</p>
      <h3>{t('Where your words go')}</h3>
      <ul>
        <li>{t('What you send goes to Anthropic, the company that runs the AI, in the United States.')}</li>
        <li>{t('Before it leaves our server, we remove names, email addresses, phone numbers, postcodes and dates from what you type.')}</li>
        <li>{t("A learner's scores and other details are sent only if you tick them. Their name and age are never sent.")}</li>
        <li>{t('Researched answers search the web. You can see the search terms afterwards.')}</li>
        <li>{t('Anthropic does not use what you send to train its AI.')}</li>
        <li>{t('After every request you can open "Where did my data go?" to see exactly what was sent.')}</li>
      </ul>
      <h3>{t('Credits')}</h3>
      <p>{t('Each request uses credits for what it actually cost. Researched answers and new Studio games use more than quick questions, so we cannot tell you the exact cost before you ask. Your history shows what each request used.')}</p>
      <p className="field-hint">
        {t('More detail is in the privacy notice.')} <Link to="/privacy" target="_blank" rel="noopener">{t('Read the privacy notice')}</Link>
      </p>
      {error && <div className="error" role="alert">{error}</div>}
      <label className="check">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
        {t('I have read this and understand the risks.')}
      </label>
      <div className="toolbar">
        <button type="button" className="btn" disabled={!agreed || busy} onClick={() => void accept()}>{t('Agree and continue')}</button>
      </div>
    </section>
  );
}
